/**
 * Shared helpers for the pesticide screens (product, substance, regulation).
 *
 * MRL values arrive from the EU Pesticides Database as free text:
 *   "10"               a specific MRL in mg/kg
 *   "0.01*"            MRL set at the limit of analytical determination (LOD)
 *   "No MRL required"  Annex IV substance, no limit applies
 *   "" / null          no value published for this combination
 */

export type MrlKind = 'set' | 'lod' | 'exempt' | 'none';

export interface ParsedMrl {
  raw: string | null;
  kind: MrlKind;
  /** Numeric limit in mg/kg (LOD included). Null for exempt / none. */
  value: number | null;
}

export function parseMrl(raw: string | null | undefined): ParsedMrl {
  const s = (raw ?? '').trim();
  if (!s || s === '-') return { raw: null, kind: 'none', value: null };
  if (/no\s+mrl\s+required/i.test(s)) return { raw: s, kind: 'exempt', value: null };
  const lod = s.endsWith('*');
  const n = Number(s.replace('*', '').replace(',', '.').trim());
  if (!Number.isFinite(n)) return { raw: s, kind: 'none', value: null };
  return { raw: s, kind: lod ? 'lod' : 'set', value: n };
}

/** Display form of an MRL cell. */
export function mrlText(m: ParsedMrl): string {
  if (m.kind === 'exempt') return 'No MRL required';
  return m.raw ?? '—';
}

/* ── Residue-name markers ───────────────────────────────────────────
   The database appends legend markers to residue names, e.g.
   "Chlorpyrifos  (F)". Only the bare markers are stripped; chemical
   names such as "(R)-2-…" stay intact because a marker must be
   followed by whitespace or the end of the string. */
const MARKER_RE = /\s*\((F|R|A|\+\+|\+)\)(?=\s|$)/g;

export const MARKER_TITLES: Record<string, string> = {
  F: 'Fat-soluble residue',
  R: 'Residue definition differs for some commodities',
};

export function splitResidueName(name: string | null | undefined): { name: string; markers: string[] } {
  const src = (name ?? '').trim();
  const markers: string[] = [];
  const clean = src
    .replace(MARKER_RE, (_m, t: string) => {
      if (!markers.includes(t)) markers.push(t);
      return '';
    })
    .replace(/\s{2,}/g, ' ')
    .trim();
  return { name: clean || src, markers };
}

/** Compact toxicology value for table cells: "0.5 mg/kg bw/day" -> "0.5". */
export function shortTox(v: string | null | undefined): string {
  const s = (v ?? '').trim();
  if (!s) return '—';
  if (/^not\s+appl/i.test(s)) return 'n/a';
  const out = s
    .replace(/\s*mg\s*\/\s*kg\s*bw(\s*(\/|per)\s*day)?/i, '')
    .replace(/(\d)\s+None\s*$/i, '$1')
    .trim();
  return out || '—';
}

/* ── Compliance verdicts ────────────────────────────────────────────── */

export type Verdict = 'pass' | 'limit' | 'fail' | 'exempt' | 'nolimit' | 'inconclusive';

export interface VerdictResult {
  verdict: Verdict;
  /** Share of the limit used, rounded. Null when there is no numeric limit. */
  pct: number | null;
}

/** A lab result: a number, or an upper bound when the lab reported "<LOQ". */
export interface LabValue {
  value: number;
  /** True for "<0.05": the residue sits somewhere below this number. */
  lessThan: boolean;
}

const EPS = 1e-9;

/**
 * Parse a lab value typed by the user.
 *   "0,4"    -> 0.4
 *   "nd"     -> 0 (not detected)
 *   "<0.05"  -> upper bound 0.05, flagged lessThan
 * Anything else is rejected, so the row says "not a number" instead of showing
 * a verdict built on a guess.
 */
export function parseUserValue(input: string): LabValue | null {
  const t = input.trim().replace(',', '.');
  if (!t) return null;
  if (/^(nd|n\.?d\.?|not detected)$/i.test(t)) return { value: 0, lessThan: false };
  const lessThan = t.startsWith('<');
  const num = lessThan ? t.slice(1).trim() : t;
  if (!/^\d*\.?\d+(e-?\d+)?$/i.test(num)) return null;
  const n = Number(num);
  if (!Number.isFinite(n) || n < 0) return null;
  return { value: n, lessThan };
}

export function judge(lab: LabValue, mrl: ParsedMrl): VerdictResult {
  if (mrl.kind === 'exempt') return { verdict: 'exempt', pct: null };
  if (mrl.value == null || mrl.value <= 0) return { verdict: 'nolimit', pct: null };
  const pct = Math.round((lab.value / mrl.value) * 100);
  // A "<LOQ" result only proves compliance when the LOQ itself sits at or
  // under the MRL. Above it, the true residue could be either side of the limit.
  if (lab.lessThan) {
    return lab.value <= mrl.value + EPS
      ? { verdict: 'pass', pct }
      : { verdict: 'inconclusive', pct };
  }
  if (lab.value > mrl.value + EPS) return { verdict: 'fail', pct };
  if (Math.abs(lab.value - mrl.value) <= EPS) return { verdict: 'limit', pct: 100 };
  return { verdict: 'pass', pct };
}

export function verdictLabel(r: VerdictResult): string {
  switch (r.verdict) {
    case 'pass':    return `Pass · ${r.pct}%`;
    case 'limit':   return 'At limit · 100%';
    case 'fail':    return `Fail · ${r.pct}%`;
    case 'exempt':  return 'No MRL required';
    case 'inconclusive': return `LOQ above MRL · ${r.pct}%`;
    default:        return 'No limit set';
  }
}

/* ── Jurisdictions (GET /api/product?jurisdictions=, GET /api/jurisdictions) ──
   One limit per selected register per substance. A jurisdiction whose
   commodity is not mapped to the product is `unmapped`: its cells are "not
   mapped", never "no limit", and never produce a pass. */

export interface MappingInfo {
  method: string | null;
  confidence: number | null;
  reviewed: boolean;
}

export interface Limit {
  value_text: string | null;
  value_num: number | null;
  is_lod: boolean;
  is_exempt: boolean;
  is_temporary: boolean;
  category: string | null;
  legal_ref: string | null;
  reference_url: string | null;
  effective_date: string | null;
  entry_into_force_date: string | null;
  expiry_date: string | null;
  footnote: string | null;
  applicability: string | null;
  residue_definition: string | null;
  commodity_name: string | null;
  mapping: MappingInfo | null;
  upcoming: {
    value_text: string | null;
    effective_date: string | null;
    legal_ref: string | null;
    reference_url: string | null;
  } | null;
  alternates: Array<{ commodity_name: string; value_text: string | null; legal_ref: string | null; category: string | null }>;
}

export interface GroupLimit {
  pesticide_residue_id: string;
  substance_name: string;
  group_commodity: string;
  group_code: string | null;
  qualifier: string | null;
  value_text: string | null;
  value_num: number | null;
  is_exempt: boolean;
  category: string;
  legal_ref: string;
  reference_url: string | null;
  effective_date: string | null;
  expiry_date: string | null;
  via: 'product' | 'ancestor' | 'membership';
}

export interface JurisdictionMeta {
  code: string;
  name: string;
  authority: string | null;
  source_url: string | null;
  licence: string | null;
  attribution: string | null;
  non_commercial_only: boolean;
  last_loaded_at: string | null;
  mapped: boolean;
  unmapped: boolean;
  pending_review: boolean;
  commodity: { source_key: string; name: string; match_method: string | null; match_confidence: number | null } | null;
  inherited_from: { product_id: string; product_name: string | null } | null;
  alternates: string[];
  group_limits: GroupLimit[];
  unmatched_substances: Array<{ substance_key: string; name: string; value_text: string | null; legal_ref: string }>;
  unmatched_total: number;
}

export interface JurisdictionInfo {
  code: string;
  name: string;
  authority: string | null;
  source_url: string | null;
  licence: string | null;
  attribution: string | null;
  non_commercial_only: boolean;
  last_loaded_at: string | null;
  expected_refresh_days: number;
  last_source_version: string | null;
  counts: {
    limits: number;
    substances: number;
    substances_mapped: number;
    commodities_total: number;
    commodities_mapped: number;
    pending_review: number;
  } | null;
}

/** A jurisdiction limit as the ParsedMrl that `judge` expects. */
export function limitToParsed(l: Limit | null | undefined): ParsedMrl {
  if (!l) return { raw: null, kind: 'none', value: null };
  if (l.is_exempt) return { raw: l.value_text || 'exempt', kind: 'exempt', value: null };
  if (l.value_num != null && Number.isFinite(l.value_num)) {
    return { raw: l.value_text || String(l.value_num), kind: l.is_lod ? 'lod' : 'set', value: l.value_num };
  }
  return parseMrl(l.value_text);
}

/* ── Lab report paste ───────────────────────────────────────────────── */

export interface LabLine {
  line: string;
  name: string;
  value: LabValue;
  /** The value exactly as pasted, e.g. "<0.05" or "0,4". */
  token: string;
}

/**
 * One result per line: a substance name followed by a value, separated by a
 * tab, semicolon, colon, comma or spaces. An optional "mg/kg" unit is ignored.
 *   "Captan\t2.1"   "Acetamiprid; 0,4 mg/kg"   "Glyphosate  <0.05"
 */
export function parseLabReport(text: string): { parsed: LabLine[]; skipped: string[] } {
  const parsed: LabLine[] = [];
  const skipped: string[] = [];
  const re = /^(.*?)[\s;:,=]+(nd|n\.d\.?|<\s*\d+(?:[.,]\d+)?|\d+(?:[.,]\d+)?)\s*(mg\s*\/\s*kg)?\s*$/i;
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const m = line.match(re);
    const value = m ? parseUserValue(m[2]) : null;
    if (!m || !m[1].trim() || value == null) {
      skipped.push(line);
      continue;
    }
    parsed.push({ line, name: m[1].trim(), value, token: m[2].trim() });
  }
  return { parsed, skipped };
}

/** Normalise a substance name for matching: lower case, no parentheticals or punctuation. */
export function normName(s: string): string {
  return s
    .toLowerCase()
    .replace(/\(.*?\)/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/* ── Display helpers ─────────────────────────────────────────────────── */

/** "Commission Regulation (EU) 2026/140" -> "Reg. (EU) 2026/140". */
export function shortReg(reg: string | null | undefined): string {
  const s = (reg ?? "").trim();
  if (!s) return "—";
  return s.replace(/^(Commission\s+)?(Implementing\s+)?Regulation\s*/i, 'Reg. ').replace(/^Reg\.\s*\(/, 'Reg. (');
}

/** Category rows arrive shouted ("FRUITS, FRESH or FROZEN; TREE NUTS"): sentence-case them. */
export function tidyName(name: string | null | undefined): string {
  const s = (name ?? "").trim();
  const letters = s.replace(/[^A-Za-z]/g, "");
  const upper = s.replace(/[^A-Z]/g, "");
  if (letters.length > 3 && upper.length / letters.length > 0.6) {
    const lower = s.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }
  return s;
}
